import { useContext } from "react";
import { AppContext, Layer, Line } from "../../../context/context";
import { useEditLayer } from "../../layer_panel/functions/layerFunctions";
import { Polyline, CircleMarker, Marker, Popup } from "react-leaflet";
import { v4 as uuid4 } from "uuid";
import { LatLngTuple, Icon, DivIcon, Point as LeafletPoint } from "leaflet";
import React from "react";
import { logEvent } from '../../../services/analytics';
import { isFixedRouteLine, type FixedRoute, type FixedRouteStop } from '../../../types/fixedRoutes';
import { isOsmRouteLine, getOsmRouteDefaultColor } from '../../../types/osmRoutes';
import { useFixedRoutes } from '../../../context/FixedRoutesContext';
import { getDistanceMeters } from '../utils/distance';
import { segmentIndexForWaypoint } from '../../../utils/fixedRoutes/waypointSegmentMapping';
import { calculateRouteGeometry as osrmCalculateRouteGeometry } from '../../../services/routing/osrmClient';
import { calculateRouteGeometry as hereCalculateRouteGeometry } from '../../../services/routing/hereClient';
import { getResolvedMissionControlLine, isTripLineShape } from './missionControlTripStyles';

// Create arrow icon with rotation
const createArrowIcon = (color: string, angle: number) => {
    return new DivIcon({
        className: 'custom-arrow',
        html: `<div style="
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-bottom: 16px solid ${color};
            transform: rotate(${angle}deg);
        "></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
    });
};

/**
 * Memoized popup body for an OSM transit route. React-leaflet keeps the
 * popup children in the React tree even when the popup is closed, so this
 * being React.memo means hundreds of routes don't each rebuild the JSX on
 * every parent re-render (e.g. during a map pan).
 */
const OsmRoutePopupContent = React.memo(function OsmRoutePopupContent({
    meta,
}: {
    meta: import('../../../types/osmRoutes').OsmRouteMetadata;
}) {
    const swatch = meta.colour || getOsmRouteDefaultColor(meta.mode);
    return (
        <div style={{ minWidth: 240, fontFamily: 'inherit' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{
                    display: 'inline-block',
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    backgroundColor: swatch,
                    border: '1px solid rgba(0,0,0,0.15)',
                    flexShrink: 0,
                }} />
                <strong style={{ fontSize: 14 }}>
                    {meta.ref ? `${meta.ref} — ${meta.routeName}` : meta.routeName}
                </strong>
            </div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#666', marginBottom: 8 }}>
                {meta.mode.replace('_', ' ')}{meta.network ? ` · ${meta.network}` : ''}
            </div>
            <table style={{ fontSize: 12, borderCollapse: 'collapse', width: '100%' }}>
                <tbody>
                    {meta.operator && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>Operator</td><td>{meta.operator}</td></tr>)}
                    {meta.from && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>From</td><td>{meta.from}</td></tr>)}
                    {meta.to && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>To</td><td>{meta.to}</td></tr>)}
                    {meta.via && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>Via</td><td>{meta.via}</td></tr>)}
                    {meta.interval && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>Interval</td><td>{meta.interval} min</td></tr>)}
                    {meta.openingHours && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>Hours</td><td>{meta.openingHours}</td></tr>)}
                    {meta.duration && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>Duration</td><td>{meta.duration}</td></tr>)}
                    {meta.stopCount > 0 && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>Stops</td><td>{meta.stopCount}</td></tr>)}
                    {meta.wheelchair && meta.wheelchair !== 'unknown' && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>Wheelchair</td><td>{meta.wheelchair}</td></tr>)}
                </tbody>
            </table>
            <div style={{ marginTop: 8, fontSize: 11, color: '#888' }}>
                Data from OpenStreetMap (OSM ID {meta.osmRouteId})
            </div>
        </div>
    );
});

/**
 * Find the closest point on a line segment to a given point
 * Returns the closest point and the segment index where it should be inserted
 */
function findClosestPointOnLine(
    point: LatLngTuple,
    positions: LatLngTuple[]
): { closestPoint: LatLngTuple; segmentIndex: number } | null {
    if (positions.length < 2) return null;
    
    let minDistance = Infinity;
    let closestPoint: LatLngTuple | null = null;
    let segmentIndex = 0;
    
    for (let i = 0; i < positions.length - 1; i++) {
        const [x1, y1] = positions[i];
        const [x2, y2] = positions[i + 1];
        const [px, py] = point;
        
        // Vector from line start to end
        const dx = x2 - x1;
        const dy = y2 - y1;
        
        // If line segment is a point, skip
        if (dx === 0 && dy === 0) continue;
        
        // Calculate parameter t for closest point on line segment
        const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)));
        
        // Closest point on line segment
        const closestX = x1 + t * dx;
        const closestY = y1 + t * dy;
        const closest: LatLngTuple = [closestX, closestY];
        
        // Distance to closest point
        const distance = Math.sqrt((px - closestX) ** 2 + (py - closestY) ** 2);
        
        if (distance < minDistance) {
            minDistance = distance;
            closestPoint = closest;
            segmentIndex = i;
        }
    }
    
    return closestPoint ? { closestPoint, segmentIndex } : null;
}

/**
 * Calculate distance between two points in degrees (simple Euclidean distance)
 */
function calculateDistance(point1: LatLngTuple, point2: LatLngTuple): number {
    const dx = point1[0] - point2[0];
    const dy = point1[1] - point2[1];
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Minimum distance between vertices in meters (to prevent vertices too close together)
 */
const MIN_VERTEX_DISTANCE_METERS = 10;

/**
 * Tolerance for matching coordinates to stop locations in meters
 * Vertices within this distance of a stop are considered "at the stop"
 */
const STOP_TOLERANCE_METERS = 10;

/**
 * Check if a coordinate is near any stop location for a route
 * @param coord Coordinate to check [lat, lng]
 * @param route Route to check stops for
 * @param allStops All available stops
 * @returns true if coordinate is within tolerance of any stop
 */
function isNearStop(
    coord: [number, number],
    route: FixedRoute,
    allStops: FixedRouteStop[]
): boolean {
    if (!route.stops || route.stops.length === 0) return false;
    
    for (const stopConfig of route.stops) {
        const stop = allStops.find(s => s.id === stopConfig.stopId);
        if (!stop || !stop.coordinates) continue;
        
        const distance = getDistanceMeters(
            coord,
            [stop.coordinates.lat, stop.coordinates.lng]
        );
        
        if (distance < STOP_TOLERANCE_METERS) {
            return true;
        }
    }
    
    return false;
}

/**
 * Check if a vertex index is in the return segment of a circulator route
 * For circulator routes, the return segment is from the last stop back to the first stop
 * @param vertexIndex Index of the vertex in the geometry coordinates
 * @param route Route to check
 * @param geometryLength Total length of geometry coordinates
 * @returns true if vertex is in the return segment
 */
function isInReturnSegment(
    vertexIndex: number,
    route: FixedRoute,
    geometryLength: number
): boolean {
    if (!route.isCirculator || !route.stops || route.stops.length === 0) {
        return false;
    }
    
    // For circulator routes, the return segment is typically the last segment
    // connecting the last stop back to the first stop
    // We consider vertices in the last portion of the geometry as part of the return segment
    // This is a heuristic - actual implementation depends on how geometry is structured
    const returnSegmentStart = Math.floor(geometryLength * 0.8); // Last 20% of geometry
    return vertexIndex >= returnSegmentStart;
}

/**
 * Check if a new vertex position violates minimum distance constraints
 * @param newCoord New coordinate to insert
 * @param existingCoords Existing coordinates
 * @param insertIndex Index where new coordinate will be inserted
 * @returns true if minimum distance is violated
 */
function violatesMinimumDistance(
    newCoord: [number, number],
    existingCoords: [number, number][],
    insertIndex: number
): boolean {
    // Check distance to previous vertex
    if (insertIndex > 0) {
        const prevCoord = existingCoords[insertIndex - 1];
        const distance = getDistanceMeters(newCoord, prevCoord);
        if (distance < MIN_VERTEX_DISTANCE_METERS) {
            return true;
        }
    }
    
    // Check distance to next vertex
    if (insertIndex < existingCoords.length) {
        const nextCoord = existingCoords[insertIndex];
        const distance = getDistanceMeters(newCoord, nextCoord);
        if (distance < MIN_VERTEX_DISTANCE_METERS) {
            return true;
        }
    }
    
    return false;
}

/**
 * Get stop coordinates for a route
 * @param route Route to get stops for
 * @param allStops All available stops
 * @returns Array of stop coordinates [lat, lng]
 */
function getRouteStopCoordinates(
    route: FixedRoute,
    allStops: FixedRouteStop[]
): [number, number][] {
    if (!route.stops || route.stops.length === 0) return [];
    
    return route.stops
        .map(stopConfig => {
            const stop = allStops.find(s => s.id === stopConfig.stopId);
            if (!stop || !stop.coordinates) return null;
            return [stop.coordinates.lat, stop.coordinates.lng] as [number, number];
        })
        .filter((coord): coord is [number, number] => coord !== null);
}

/**
 * Route between two coordinate points (not stops)
 * Creates temporary stop objects for routing
 */
async function routeBetweenCoordinates(
    fromCoord: [number, number],
    toCoord: [number, number],
    route: FixedRoute
): Promise<[number, number][] | null> {
    // Validate coordinates
    if (!fromCoord || !toCoord || 
        !Array.isArray(fromCoord) || !Array.isArray(toCoord) ||
        fromCoord.length < 2 || toCoord.length < 2) {
        console.error('[MapLineFunctions] Invalid coordinates for routing:', { fromCoord, toCoord });
        return null;
    }
    
    const fromLat = fromCoord[0];
    const fromLng = fromCoord[1];
    const toLat = toCoord[0];
    const toLng = toCoord[1];
    
    // Validate coordinate values
    if (typeof fromLat !== 'number' || typeof fromLng !== 'number' ||
        typeof toLat !== 'number' || typeof toLng !== 'number' ||
        isNaN(fromLat) || isNaN(fromLng) || isNaN(toLat) || isNaN(toLng) ||
        !isFinite(fromLat) || !isFinite(fromLng) || !isFinite(toLat) || !isFinite(toLng)) {
        console.error('[MapLineFunctions] Invalid coordinate values:', { fromCoord, toCoord });
        return null;
    }
    
    // Validate coordinate ranges
    if (fromLat < -90 || fromLat > 90 || fromLng < -180 || fromLng > 180 ||
        toLat < -90 || toLat > 90 || toLng < -180 || toLng > 180) {
        console.error('[MapLineFunctions] Coordinates out of valid range:', { fromCoord, toCoord });
        return null;
    }
    
    // Create temporary stop objects for routing
    const fromStop: FixedRouteStop = {
        id: 'temp-from',
        name: 'Temp From',
        coordinates: { lat: fromLat, lng: fromLng }
    };
    
    const toStop: FixedRouteStop = {
        id: 'temp-to',
        name: 'Temp To',
        coordinates: { lat: toLat, lng: toLng }
    };
    
    try {
        if (route.isBusRoute) {
            return await hereCalculateRouteGeometry(fromStop, toStop, true);
        } else {
            return await osrmCalculateRouteGeometry(fromStop, toStop, 'driving');
        }
    } catch (error) {
        console.error('[MapLineFunctions] Failed to route between coordinates:', error, { fromCoord, toCoord });
        return null;
    }
}

/**
 * Route between all waypoints to generate full geometry
 * This is the core function for waypoint-based routing
 */
async function routeBetweenWaypoints(
    waypoints: [number, number][],
    route: FixedRoute
): Promise<[number, number][] | null> {
    if (waypoints.length < 2) {
        return waypoints.length === 1 ? [waypoints[0]] : null;
    }
    
    // Route segments in parallel for better performance
    const segmentPromises: Promise<[number, number][] | null>[] = [];
    
    for (let i = 0; i < waypoints.length - 1; i++) {
        const fromCoord = waypoints[i];
        const toCoord = waypoints[i + 1];
        segmentPromises.push(routeBetweenCoordinates(fromCoord, toCoord, route));
    }
    
    // Wait for all segments to route in parallel
    const segmentResults = await Promise.all(segmentPromises);
    
    // Combine segments into full geometry
    const routedSegments: [number, number][] = [];
    for (let i = 0; i < segmentResults.length; i++) {
        const segmentGeometry = segmentResults[i];
        
        if (segmentGeometry && segmentGeometry.length > 0) {
            // Add the routed segment (excluding the last point to avoid duplication)
            if (i === 0) {
                // First segment: include all points
                routedSegments.push(...segmentGeometry);
            } else {
                // Subsequent segments: skip first point (already added)
                routedSegments.push(...segmentGeometry.slice(1));
            }
        } else {
            // If routing fails, use straight line as fallback
            const fromCoord = waypoints[i];
            const toCoord = waypoints[i + 1];
            if (i === 0) {
                routedSegments.push(fromCoord, toCoord);
            } else {
                routedSegments.push(toCoord);
            }
        }
    }
    
    // For circulator routes, also route from last waypoint back to first
    if (route.isCirculator && waypoints.length >= 2) {
        const fromCoord = waypoints[waypoints.length - 1];
        const toCoord = waypoints[0];
        
        const returnSegmentGeometry = await routeBetweenCoordinates(fromCoord, toCoord, route);
        
        if (returnSegmentGeometry && returnSegmentGeometry.length > 0) {
            // Skip first point (already added as last waypoint)
            routedSegments.push(...returnSegmentGeometry.slice(1));
        } else {
            // Fallback: straight line
            routedSegments.push(toCoord);
        }
    }
    
    return routedSegments.length > 0 ? routedSegments : waypoints;
}

/**
 * Route a single segment between two waypoints
 * Used for incremental updates when a waypoint is moved
 */
async function routeWaypointSegment(
    fromWaypointIndex: number,
    toWaypointIndex: number,
    waypoints: [number, number][],
    route: FixedRoute
): Promise<[number, number][] | null> {
    if (fromWaypointIndex < 0 || toWaypointIndex >= waypoints.length || fromWaypointIndex >= toWaypointIndex) {
        return null;
    }
    
    const fromCoord = waypoints[fromWaypointIndex];
    const toCoord = waypoints[toWaypointIndex];
    
    return await routeBetweenCoordinates(fromCoord, toCoord, route);
}

/**
 * Route only affected segments when a waypoint is moved
 * This is much faster than routing the entire route
 * 
 * @param waypointIndex - Index of the waypoint that was moved
 * @param waypoints - Updated waypoints array
 * @param route - Route object
 * @param existingGeometry - Current geometry to merge with (optional, for performance)
 * @returns New geometry with only affected segments re-routed
 */
async function routeAffectedSegments(
    waypointIndex: number,
    waypoints: [number, number][],
    route: FixedRoute,
    existingGeometry?: [number, number][]
): Promise<[number, number][] | null> {
    if (waypoints.length < 2) {
        return waypoints.length === 1 ? [waypoints[0]] : null;
    }
    
    // For routes with many waypoints, only route the 2 affected segments
    // For routes with few waypoints, route all (it's fast anyway)
    if (waypoints.length <= 3) {
        // Small routes: route all segments (fast enough)
        return routeBetweenWaypoints(waypoints, route);
    }
    
    // Determine which segments need re-routing
    const segmentsToRoute: Array<{ from: number; to: number }> = [];
    
    // Route from previous waypoint to dragged waypoint
    if (waypointIndex > 0) {
        segmentsToRoute.push({ from: waypointIndex - 1, to: waypointIndex });
    }
    
    // Route from dragged waypoint to next waypoint
    if (waypointIndex < waypoints.length - 1) {
        segmentsToRoute.push({ from: waypointIndex, to: waypointIndex + 1 });
    }
    
    // If no segments to route, return existing geometry or route all
    if (segmentsToRoute.length === 0) {
        return existingGeometry || routeBetweenWaypoints(waypoints, route);
    }
    
    // Route affected segments in parallel for speed (no delays between them)
    const segmentPromises = segmentsToRoute.map(({ from, to }) =>
        routeWaypointSegment(from, to, waypoints, route)
    );
    
    const routedSegments = await Promise.all(segmentPromises);
    
    // Check if routing succeeded
    const hasValidSegments = routedSegments.every(seg => seg && seg.length > 0);
    if (!hasValidSegments) {
        // If routing failed, fall back to full routing
        console.warn('[MapLineFunctions] Some segments failed to route, falling back to full routing');
        return routeBetweenWaypoints(waypoints, route);
    }
    
    // If we have existing geometry, merge the newly routed segments with existing geometry
    if (existingGeometry && existingGeometry.length > 0) {
        // Find waypoint positions in existing geometry by finding closest points
        const waypointGeometryIndices: number[] = [];
        for (const waypoint of waypoints) {
            let closestIdx = 0;
            let minDist = Infinity;
            for (let i = 0; i < existingGeometry.length; i++) {
                const dist = getDistanceMeters(waypoint, existingGeometry[i]);
                if (dist < minDist) {
                    minDist = dist;
                    closestIdx = i;
                }
            }
            waypointGeometryIndices.push(closestIdx);
        }
        
        // Build merged geometry
        const mergedGeometry: [number, number][] = [];
        
        // Add geometry before first affected segment
        if (segmentsToRoute.length > 0) {
            const firstAffectedSegment = segmentsToRoute[0];
            const beforeEndIdx = waypointGeometryIndices[firstAffectedSegment.from];
            mergedGeometry.push(...existingGeometry.slice(0, beforeEndIdx + 1));
        }
        
        // Add newly routed segments
        for (let i = 0; i < segmentsToRoute.length; i++) {
            const segment = routedSegments[i];
            if (segment && segment.length > 0) {
                if (mergedGeometry.length === 0) {
                    // First segment: include all points
                    mergedGeometry.push(...segment);
                } else {
                    // Subsequent segments: skip first point (already added)
                    mergedGeometry.push(...segment.slice(1));
                }
            }
        }
        
        // Add geometry after last affected segment
        if (segmentsToRoute.length > 0) {
            const lastAffectedSegment = segmentsToRoute[segmentsToRoute.length - 1];
            const afterStartIdx = waypointGeometryIndices[lastAffectedSegment.to];
            if (afterStartIdx < existingGeometry.length) {
                // Skip first point of remaining geometry (already added as last point of routed segment)
                mergedGeometry.push(...existingGeometry.slice(afterStartIdx + 1));
            }
        }
        
        // Ensure we have valid geometry
        if (mergedGeometry.length > 0) {
            return mergedGeometry;
        }
    }
    
    // Fallback: build full geometry by routing all segments
    return routeBetweenWaypoints(waypoints, route);
}

function buildPreviewGeometryFromWaypoints(
    waypoints: [number, number][],
    isCirculator: boolean
): [number, number][] {
    if (!Array.isArray(waypoints) || waypoints.length === 0) return [];

    const preview = waypoints
        .filter(
            (wp): wp is [number, number] =>
                Array.isArray(wp) &&
                wp.length >= 2 &&
                typeof wp[0] === 'number' &&
                typeof wp[1] === 'number' &&
                !isNaN(wp[0]) &&
                !isNaN(wp[1]) &&
                isFinite(wp[0]) &&
                isFinite(wp[1])
        )
        .map((wp) => [wp[0], wp[1]] as [number, number]);

    if (isCirculator && preview.length >= 2) {
        const first = preview[0];
        const last = preview[preview.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
            preview.push([first[0], first[1]]);
        }
    }

    return preview;
}

/**
 * Get waypoint indices that correspond to stop locations
 * Returns a Set of waypoint indices that are locked (cannot be dragged)
 */
function getWaypointIndicesForStops(
    route: FixedRoute,
    allStops: FixedRouteStop[],
    waypoints: [number, number][]
): Set<number> {
    const stopIndices = new Set<number>();
    
    if (!route.stops || route.stops.length === 0 || !waypoints || waypoints.length === 0) {
        return stopIndices;
    }
    
    // For each stop, find the closest waypoint
    route.stops.forEach((stopConfig, stopIndex) => {
        const stop = allStops.find(s => s.id === stopConfig.stopId);
        if (!stop || !stop.coordinates) return;
        
        const stopCoord: [number, number] = [stop.coordinates.lat, stop.coordinates.lng];
        
        // Find closest waypoint to this stop
        let closestWaypointIndex = 0;
        let minDistance = Infinity;
        
        waypoints.forEach((waypoint, idx) => {
            const distance = getDistanceMeters(stopCoord, waypoint);
            if (distance < minDistance) {
                minDistance = distance;
                closestWaypointIndex = idx;
            }
        });
        
        // If waypoint is very close to stop (within tolerance), it's a stop waypoint
        if (minDistance < STOP_TOLERANCE_METERS) {
            stopIndices.add(closestWaypointIndex);
        }
    });
    
    return stopIndices;
}

/**
 * Map a waypoint index to the stop-segment index it belongs to.
 *
 * `waypoints` interleaves stops (locked) with intermediate control points, so a
 * waypoint's position in that array is NOT the index of the stop-segment it sits in.
 * A dragged vertex belongs to the segment that follows the last stop at-or-before it,
 * i.e. (number of stop-waypoints at index <= waypointIndex) - 1.
 *
 * Passing the raw waypoint index downstream made `recalculateSegmentMetrics` refresh
 * the wrong segment's travel time/distance whenever a route had intermediate waypoints.
 */
function waypointIndexToSegmentIndex(
    route: FixedRoute,
    allStops: FixedRouteStop[],
    waypoints: [number, number][],
    waypointIndex: number
): number {
    const stopWaypointIndices = getWaypointIndicesForStops(route, allStops, waypoints);
    return segmentIndexForWaypoint(stopWaypointIndices, waypointIndex);
}

/**
 * Initialize waypoints from stops if missing
 */
function ensureWaypointsInitialized(
    route: FixedRoute,
    allStops: FixedRouteStop[]
): [number, number][] {
    if (route.waypoints && route.waypoints.length > 0) {
        return route.waypoints;
    }
    
    // Initialize from stops
    if (!route.stops || route.stops.length === 0) {
        return [];
    }
    
    return route.stops
        .map(stopConfig => {
            const stop = allStops.find(s => s.id === stopConfig.stopId);
            if (!stop || !stop.coordinates) return null;
            return [stop.coordinates.lat, stop.coordinates.lng] as [number, number];
        })
        .filter((coord): coord is [number, number] => coord !== null);
}

/**
 * Validate edited geometry coordinates
 * Ensures coordinates are valid (not NaN, within bounds, at least 2 points)
 * @param coordinates Geometry coordinates to validate
 * @returns true if valid, false otherwise
 */
function validateGeometry(coordinates: [number, number][]): boolean {
    if (!coordinates || coordinates.length < 2) {
        return false;
    }
    
    for (const coord of coordinates) {
        if (!coord || !Array.isArray(coord) || coord.length < 2) {
            return false;
        }
        
        const [lat, lng] = coord;
        
        // Check for NaN or invalid numbers
        if (typeof lat !== 'number' || typeof lng !== 'number') {
            return false;
        }
        
        if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
            return false;
        }
        
        // Check bounds: latitude -90 to 90, longitude -180 to 180
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            return false;
        }
    }
    
    return true;
}

// Function to calculate the true midpoint and its direction for a polyline
function getPolylineMidpointAndAngle(positions: LatLngTuple[]): { pos: LatLngTuple, angle: number } {
    // Calculate total length
    let total = 0;
    const segLens: number[] = [];
    for (let i = 1; i < positions.length; i++) {
        const dx = positions[i][1] - positions[i - 1][1];
        const dy = positions[i][0] - positions[i - 1][0];
        const len = Math.sqrt(dx * dx + dy * dy);
        segLens.push(len);
        total += len;
    }
    // Find midpoint
    let mid = total / 2;
    let acc = 0;
    for (let i = 0; i < segLens.length; i++) {
        if (acc + segLens[i] >= mid) {
            const remain = mid - acc;
            const ratio = remain / segLens[i];
            const lat = positions[i][0] + (positions[i + 1][0] - positions[i][0]) * ratio;
            const lng = positions[i][1] + (positions[i + 1][1] - positions[i][1]) * ratio;
            const angle = Math.atan2(positions[i + 1][0] - positions[i][0], positions[i + 1][1] - positions[i][1]) * 180 / Math.PI;
            return { pos: [lat, lng], angle };
        }
        acc += segLens[i];
    }
    // fallback
    return { pos: positions[0], angle: 0 };
}

/**
 * Calculate perpendicular offset for overlapping routes
 * Returns offset coordinates for a route based on its index and overlap detection
 */
function calculateRouteOffset(
    positions: LatLngTuple[],
    allFixedRouteLines: Array<{ line: Line; positions: LatLngTuple[]; index: number }>,
    currentIndex: number,
    overlapThreshold: number = 0.0001 // ~11 meters at equator
): LatLngTuple[] {
    // If no other routes, no offset needed
    if (allFixedRouteLines.length <= 1) {
        return positions;
    }
    
    // Check for overlaps with other routes by comparing segments
    const hasOverlap = allFixedRouteLines.some((other, idx) => {
        if (idx === currentIndex) return false;
        
        // Check if any segments are close to each other
        for (let i = 0; i < positions.length - 1; i++) {
            const segStart = positions[i];
            const segEnd = positions[i + 1];
            
            for (let j = 0; j < other.positions.length - 1; j++) {
                const otherSegStart = other.positions[j];
                const otherSegEnd = other.positions[j + 1];
                
                // Check if segment endpoints are close
                const dist1 = Math.sqrt(
                    Math.pow(segStart[0] - otherSegStart[0], 2) + 
                    Math.pow(segStart[1] - otherSegStart[1], 2)
                );
                const dist2 = Math.sqrt(
                    Math.pow(segEnd[0] - otherSegEnd[0], 2) + 
                    Math.pow(segEnd[1] - otherSegEnd[1], 2)
                );
                
                // If both endpoints are close, segments overlap
                if (dist1 < overlapThreshold && dist2 < overlapThreshold) {
                    return true;
                }
                
                // Also check if any point on one segment is close to the other segment
                const mid1: LatLngTuple = [(segStart[0] + segEnd[0]) / 2, (segStart[1] + segEnd[1]) / 2];
                const mid2: LatLngTuple = [(otherSegStart[0] + otherSegEnd[0]) / 2, (otherSegStart[1] + otherSegEnd[1]) / 2];
                const midDist = Math.sqrt(
                    Math.pow(mid1[0] - mid2[0], 2) + 
                    Math.pow(mid1[1] - mid2[1], 2)
                );
                if (midDist < overlapThreshold) {
                    return true;
                }
            }
        }
        return false;
    });
    
    // If no overlap, no offset needed
    if (!hasOverlap) {
        return positions;
    }
    
    // Calculate offset: alternate routes offset left/right
    // Offset distance in degrees (approximately 5-10 meters at mid-latitudes)
    const offsetDistance = 0.00005; // ~5.5 meters
    const offsetDirection = currentIndex % 2 === 0 ? 1 : -1; // Alternate left/right
    
    // Calculate average direction of the route
    let totalDx = 0;
    let totalDy = 0;
    let segmentCount = 0;
    
    for (let i = 1; i < positions.length; i++) {
        const dx = positions[i][1] - positions[i - 1][1]; // longitude difference
        const dy = positions[i][0] - positions[i - 1][0]; // latitude difference
        const length = Math.sqrt(dx * dx + dy * dy);
        if (length > 0) {
            totalDx += dx / length; // normalized direction
            totalDy += dy / length;
            segmentCount++;
        }
    }
    
    // If we have segments, calculate perpendicular offset
    if (segmentCount > 0) {
        const avgDx = totalDx / segmentCount;
        const avgDy = totalDy / segmentCount;
        
        // Perpendicular vector (rotate 90 degrees)
        // For a vector (dx, dy), perpendicular is (-dy, dx) or (dy, -dx)
        const perpDx = -avgDy * offsetDirection;
        const perpDy = avgDx * offsetDirection;
        
        // Normalize and scale
        const perpLength = Math.sqrt(perpDx * perpDx + perpDy * perpDy);
        if (perpLength > 0) {
            const normalizedDx = (perpDx / perpLength) * offsetDistance;
            const normalizedDy = (perpDy / perpLength) * offsetDistance;
            
            // Apply offset to all positions
            return positions.map(pos => [
                pos[0] + normalizedDy, // latitude
                pos[1] + normalizedDx  // longitude
            ] as LatLngTuple);
        }
    }
    
    // Fallback: simple offset based on index
    const simpleOffset = offsetDistance * offsetDirection;
    return positions.map(pos => [
        pos[0] + simpleOffset,
        pos[1]
    ] as LatLngTuple);
}

// Function to calculate arrow positions and angles
const calculateArrowPositions = (positions: LatLngTuple[], arrowStyle: string): {pos: LatLngTuple, angle: number}[] => {
    if (arrowStyle === 'none' || positions.length < 2) return [];
    const arrows: {pos: LatLngTuple, angle: number}[] = [];
    const getAngle = (a: LatLngTuple, b: LatLngTuple) => {
        const dx = b[1] - a[1];
        const dy = b[0] - a[0];
        return Math.atan2(dy, dx) * 180 / Math.PI;
    };
    if (arrowStyle === 'start') {
        arrows.push({ pos: positions[0], angle: getAngle(positions[0], positions[1]) });
    } else if (arrowStyle === 'end') {
        arrows.push({ pos: positions[positions.length - 1], angle: getAngle(positions[positions.length - 2], positions[positions.length - 1]) });
    } else if (arrowStyle === 'both') {
        arrows.push({ pos: positions[0], angle: getAngle(positions[0], positions[1]) });
        arrows.push({ pos: positions[positions.length - 1], angle: getAngle(positions[positions.length - 2], positions[positions.length - 1]) });
    } else if (arrowStyle === 'middle' && positions.length > 1) {
        // Place an arrow at the midpoint of each segment
        for (let i = 0; i < positions.length - 1; i++) {
            const a = positions[i];
            const b = positions[i + 1];
            const mid: LatLngTuple = [
                (a[0] + b[0]) / 2,
                (a[1] + b[1]) / 2
            ];
            const angle = getAngle(a, b);
            arrows.push({ pos: mid, angle });
        }
    } else if (arrowStyle === 'all') {
        for (let i = 1; i < positions.length; i++) {
            arrows.push({ pos: positions[i], angle: getAngle(positions[i - 1], positions[i]) });
        }
    }
    return arrows;
};

// Function to handle line movement (for editing line coordinates)
export function useLineMovement() {
    const context = useContext(AppContext);

    if (!context) {
        throw new Error("useLineMovement must be used within the context provider");
    }

    const { global } = context;
    const { mapLayers, setSelectedShapes } = global;
    const editLayer = useEditLayer();

    const moveLine = (lineId: string, newCoordinates: [number, number][], maintainSelection: boolean = true) => {
        // Find the layer containing this line
        for (const layer of mapLayers) {
            const lineIndex = layer.shapes.findIndex(shape => shape.type === 'line' && shape.id === lineId);
            if (lineIndex !== -1) {
                const updatedShapes = layer.shapes.map((shape, idx) => {
                    if (idx === lineIndex && shape.type === 'line') {
                        return {
                            ...shape,
                            coordinates: newCoordinates
                        };
                    }
                    return shape;
                });

                const updatedLayer: Layer = {
                    ...layer,
                    shapes: updatedShapes
                };

                editLayer(updatedLayer, layer.layerId);
                
                if (maintainSelection) {
                    setSelectedShapes([lineId]);
                }
                return;
            }
        }
    };

    return moveLine;
}

// Function to add a completed line to the active layer
export function useAddLine() {
    const context = useContext(AppContext);
    const editLayer = useEditLayer();

    if (!context) {
        throw new Error("useAddLine must be used within the context provider")
    }

    const { global } = context;
    const { activeLayerId, mapLayers } = global;

    return (coordinates: [number, number][]) => {
        // Use active layer or default to the last layer
        const newActiveLayerId: number = !activeLayerId ? mapLayers.length - 1 : (activeLayerId as number);
        
        if (!activeLayerId) {
            global.setActiveLayerId(mapLayers.length - 1);
        }

        // Create the new line
        const newLine: Line = {
            id: uuid4(),
            layerId: newActiveLayerId,
            type: 'line',
            name: 'New Line',
            description: '',
            colour: '#3388ff', // Default blue color
            opacity: '0.5',
            borderColour: '#3388ff',
            borderWidth: 2,
            isLocked: false,
            coordinates: coordinates,
            dashPattern: 'none',
            arrowStyle: 'none'
        };

        // --- Analytics: Log line creation ---
        logEvent({
            category: 'Shape',
            action: 'CreateLine',
            label: 'Line Created',
            vertexCount: coordinates.length,
            coordinates: coordinates
        });

        // Update the layer with the new line
        const layer: Layer = mapLayers.filter(l => l.layerId === newActiveLayerId)[0];
        const newShapes = [...layer.shapes, newLine];
        const newLayer: Layer = {
            ...layer,
            shapes: newShapes
        };

        editLayer(newLayer, newActiveLayerId);
    };
}

// Function to render all lines on the map
export function useRenderLines() {
    const context = useContext(AppContext);
    const editLayer = useEditLayer();
    const moveLine = useLineMovement();
    const { selectedRouteId, editingRouteId, routes, updateRoute, stops, setRouteGenerationStatus } = useFixedRoutes();
    
    // Store routes reference to avoid stale closures
    const routesRef = React.useRef(routes);
    React.useEffect(() => {
        routesRef.current = routes;
    }, [routes]);
    
    // Track which route is currently recalculating geometry
    const [recalculatingRouteId, setRecalculatingRouteId] = React.useState<string | null>(null);
    const [dragPreviewByRouteId, setDragPreviewByRouteId] = React.useState<Record<string, [number, number][]>>({});

    // Routes whose circulator return leg is currently loading. Set when the circulator toggle
    // fires ('fixedRouteReturnLegLoading' event from RouteBuilder); cleared when the real return
    // geometry arrives and closes the loop, or after a safety timeout. This is a genuine transient
    // loading signal, so the dotted "back to origin" line can never get stuck on a settled route.
    const [returnLegLoadingIds, setReturnLegLoadingIds] = React.useState<Record<string, true>>({});

    React.useEffect(() => {
        const onReturnLegLoading = (e: Event) => {
            const id = (e as CustomEvent<{ routeId?: string }>).detail?.routeId;
            if (!id) return;
            setReturnLegLoadingIds((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
            // Safety net: drop it after 20s even if routing never closes the loop, so a failed
            // return leg doesn't leave a dotted line throbbing forever.
            window.setTimeout(() => {
                setReturnLegLoadingIds((prev) => {
                    if (!prev[id]) return prev;
                    const next = { ...prev };
                    delete next[id];
                    return next;
                });
            }, 20000);
        };
        window.addEventListener('fixedRouteReturnLegLoading', onReturnLegLoading as EventListener);
        return () => window.removeEventListener('fixedRouteReturnLegLoading', onReturnLegLoading as EventListener);
    }, []);

    // Clear the loading flag as soon as the route's real return-leg geometry arrives (it has
    // replaced the dotted line), or the route stops being a circulator. Keying off
    // returnSegmentGeometry presence is reliable — unlike a loop-closure coordinate check, which
    // can stay "open" by a hair and leave the dotted line stuck.
    React.useEffect(() => {
        setReturnLegLoadingIds((prev) => {
            const ids = Object.keys(prev);
            if (ids.length === 0) return prev;
            let changed = false;
            const next: Record<string, true> = {};
            for (const id of ids) {
                const r = routes.find((rt) => rt.id === id);
                const returnReady = !!(r?.geometry?.returnSegmentGeometry && r.geometry.returnSegmentGeometry.length > 0);
                if (!r || !r.isCirculator || returnReady) {
                    changed = true; // return leg arrived (or no longer a circulator) -> stop the loader
                    continue;
                }
                next[id] = true;
            }
            return changed ? next : prev;
        });
    }, [routes]);

    if (!context) {
        throw new Error("useRenderLines must be used within the AppContext provider");
    }

    const { global } = context;
    const { mapLayers, setSelectedShapes } = global;

    return () => {
        // First pass: Collect all fixed route lines from Fixed Routes layer for offset calculation
        const fixedRoutesLayer = mapLayers.find(layer => layer.layerName === 'Fixed Routes' && !layer.hidden);
        const allFixedRouteLines: Array<{ line: Line; positions: LatLngTuple[]; index: number }> = [];
        
        if (fixedRoutesLayer) {
            fixedRoutesLayer.shapes.forEach((shape, idx) => {
                if (shape.type === 'line' && !shape.hidden && isFixedRouteLine(shape as Line)) {
                    const line = shape as Line;
                    let positions: LatLngTuple[] = line.coordinates.map(coord => [coord[0], coord[1]] as LatLngTuple);
                    allFixedRouteLines.push({ line, positions, index: idx });
                }
            });
        }
        
        // Reverse layers array so top layer in menu renders on top visually
        return mapLayers
            .filter(layer => !layer.hidden)
            .slice()
            .reverse()
            .flatMap((layer, layerIndex) =>
                // Reverse shapes array so top shape in menu renders on top visually
                layer.shapes.slice().reverse().map((shape, shapeIndex) => {
                    if (shape.type === 'line' && !shape.hidden) {
                        const line = shape as Line;

                        // Trip lines (origin→dest imports) are handled by TripLayerRenderer
                        // with canvas rendering, viewport culling, and a render cap.
                        // Skip them here to avoid double-rendering.
                        if (isTripLineShape(line)) return null;
                        // OSM transit routes are drawn on a canvas by
                        // OsmRouteLayerRenderer (with viewport culling + render cap)
                        // to keep pan/zoom fluid when an analysis pulls 1000+ routes.
                        if (isOsmRouteLine(line)) return null;

                        const isSelected = global.selectedShapes.includes(line.id);
                        
                        // Check if this is a fixed route line and if it's selected
                        let isFixedRouteSelected = false;
                        let isFixedRoute = false;
                        let fixedRouteIndex = -1;
                        if (isFixedRouteLine(line)) {
                            isFixedRoute = true;
                            const metadata = (line as any).fixedRouteMetadata;
                            if (metadata && metadata.fixedRouteId) {
                                isFixedRouteSelected = selectedRouteId === metadata.fixedRouteId;
                                // Find index in fixed routes array
                                fixedRouteIndex = allFixedRouteLines.findIndex(fr => fr.line.id === line.id);
                            }
                        }
                        
                        // Dynamic trip line logic
                        // Safely map coordinates, handling potential undefined values
                        let positions: LatLngTuple[] = line.coordinates
                          .filter(coord => coord && Array.isArray(coord) && coord.length >= 2)
                          .map(coord => {
                            const lat = coord[0];
                            const lng = coord[1];
                            // Validate before creating tuple
                            if (typeof lat === 'number' && typeof lng === 'number' && 
                                !isNaN(lat) && !isNaN(lng) && 
                                isFinite(lat) && isFinite(lng)) {
                              return [lat, lng] as LatLngTuple;
                            }
                            return null;
                          })
                          .filter((pos): pos is LatLngTuple => pos !== null);
                        if ('originPointId' in line && 'destPointId' in line) {
                            const origin = layer.shapes.find(s => s.id === (line as any).originPointId && s.type === 'point');
                            const dest = layer.shapes.find(s => s.id === (line as any).destPointId && s.type === 'point');
                            if (origin && dest && origin.type === 'point' && dest.type === 'point') {
                                positions = [
                                    [origin.coordinates[0], origin.coordinates[1]] as LatLngTuple,
                                    [dest.coordinates[0], dest.coordinates[1]] as LatLngTuple
                                ];
                            }
                        }
                        
                        // Skip rendering if no valid positions (need at least 2 for a line)
                        if (positions.length < 2) {
                            return null;
                        }
                        
                        // Apply offset for fixed routes if they overlap
                        if (isFixedRoute && fixedRouteIndex >= 0 && allFixedRouteLines.length > 1) {
                            positions = calculateRouteOffset(positions, allFixedRouteLines, fixedRouteIndex);
                        }

                        // Calculate arrow positions (use original positions for arrows, not offset)
                        const arrowPositions = calculateArrowPositions(positions, line.arrowStyle || 'none');
                        
                        // Determine line color and opacity
                        const resolvedMissionControlLine = getResolvedMissionControlLine(line, isTripLineShape(line as any));
                        const resolvedLineColor = resolvedMissionControlLine.borderColor;
                        const resolvedLineOpacity = resolvedMissionControlLine.opacity;

                        const resolvedBaseWeight = resolvedMissionControlLine.width;
                        const isOsmRouteSelected = isOsmRouteLine(line) && isSelected;
                        const resolvedLineWeight = isFixedRouteSelected
                            ? resolvedBaseWeight + 2
                            : isOsmRouteSelected
                                ? resolvedBaseWeight + 4
                                : resolvedBaseWeight;

                        const lineColor = resolvedLineColor;
                        const lineOpacity = isOsmRouteSelected ? 1 : resolvedLineOpacity;
                        const lineWeight = resolvedLineWeight;

                        // Check if this fixed route is in edit mode
                        const isInEditMode = isFixedRoute && editingRouteId && (line as any).fixedRouteMetadata?.fixedRouteId === editingRouteId;
                        const route = isFixedRoute ? routes.find(r => r.id === (line as any).fixedRouteMetadata?.fixedRouteId) : null;
                        const dragPreviewPositions = route ? dragPreviewByRouteId[route.id] : undefined;

                        // Circulator return-leg loading line: when the user turns a route into a
                        // circulator we draw a dotted, throbbing line straight back to the origin
                        // (last stop -> first stop) to signal the return leg is loading. It only
                        // shows for routes flagged as actively loading (returnLegLoadingIds, set by
                        // the toggle and self-clearing), and only while the committed geometry is
                        // still "open" (first/last points don't coincide). Once the real return
                        // geometry arrives and closes the loop it disappears and the solid route line
                        // spans the full loop. Skipped during a drag preview (which already closes
                        // the loop for circulators) to avoid double-drawing.
                        const hasReturnLegGeometry = !!(
                            route?.geometry?.returnSegmentGeometry && route.geometry.returnSegmentGeometry.length > 0
                        );
                        const firstPos = positions[0];
                        const lastPos = positions[positions.length - 1];
                        const showCirculatorReturnLoading = !!(
                            isFixedRoute && route && route.isCirculator
                            && positions.length >= 2 && !dragPreviewPositions
                            && !hasReturnLegGeometry && returnLegLoadingIds[route.id]
                        );

                        // Build event handlers
                        const eventHandlers: any = {
                            click: (e: any) => {
                                            // Check if this is a fixed route line
                                            if (isFixedRouteLine(line)) {
                                                const metadata = (line as any).fixedRouteMetadata;
                                                if (metadata && metadata.fixedRouteId) {
                                                    // Dispatch custom event for fixed route click
                                                    const customEvent = new CustomEvent('fixedRouteLineClick', {
                                                        detail: {
                                                            routeId: metadata.fixedRouteId,
                                                            routeName: metadata.routeName,
                                                        }
                                                    });
                                                    window.dispatchEvent(customEvent);
                                                    e.originalEvent.preventDefault();
                                                    return;
                                                }
                                            }
                                            
                                            // OSM transit routes: select the route line plus all its
                                            // associated stop Points so they highlight together. The
                                            // Popup attached to the click-target Polyline opens after
                                            // this handler returns.
                                            if (isOsmRouteLine(line)) {
                                                const meta = line.osmRouteMetadata;
                                                setSelectedShapes([line.id, ...(meta.stopShapeIds ?? [])]);
                                                return;
                                            }

                                            if (context.modeSettings.mode === 'Select') {
                                                e.originalEvent.preventDefault();
                                                if (e.originalEvent.ctrlKey) {
                                                    // Toggle selection with Ctrl+click while maintaining existing selection
                                                    setSelectedShapes(prev =>
                                                        prev.includes(line.id)
                                                            ? prev.filter(id => id !== line.id)  // Remove if already selected
                                                            : [...prev, line.id]  // Add to existing selection
                                                    );
                                                } else {
                                                    // Replace selection without Ctrl
                                                    setSelectedShapes([line.id]);
                                                }
                                            }
                                        },
                                        contextmenu: (e: any) => {
                                            e.originalEvent.preventDefault();
                                            const map = e.target._map;
                                            const point = map.mouseEventToContainerPoint(e.originalEvent);
                                            const latlng = map.containerPointToLatLng(point);
                                            
                                            // Check if this is a fixed route line
                                            if (isFixedRouteLine(line)) {
                                                const metadata = (line as any).fixedRouteMetadata;
                                                if (metadata && metadata.fixedRouteId) {
                                                    // Dispatch custom event for fixed route context menu
                                                    const customEvent = new CustomEvent('fixedRouteContextMenu', {
                                                        detail: {
                                                            x: e.originalEvent.clientX,
                                                            y: e.originalEvent.clientY,
                                                            line: line,
                                                            routeId: metadata.fixedRouteId,
                                                            routeName: metadata.routeName
                                                        }
                                                    });
                                                    window.dispatchEvent(customEvent);
                                                    return;
                                                }
                                            }
                                            
                                            // Create a custom event to handle regular line context menu
                                            const customEvent = new CustomEvent('lineContextMenu', {
                                                detail: {
                                                    x: e.originalEvent.clientX,
                                                    y: e.originalEvent.clientY,
                                                    line: line
                                                }
                                            });
                                            window.dispatchEvent(customEvent);
                                        },
                                        ...(isInEditMode ? {
                                            mousedown: async (e: any) => {
                                                // Only allow dragging in edit mode
                                                if (!isInEditMode || !route) return;
                                                
                                                e.originalEvent.preventDefault();
                                                e.originalEvent.stopPropagation();
                                                
                                                const map = e.target._map;
                                                const latlng = e.latlng;
                                                const clickPoint: LatLngTuple = [latlng.lat, latlng.lng];
                                                
                                                // Ensure waypoints are initialized
                                                let waypoints = ensureWaypointsInitialized(route, stops);
                                                if (waypoints.length === 0) {
                                                    return;
                                                }
                                                
                                                // Find which two waypoints the click point is between
                                                // Find the closest segment between waypoints
                                                let closestSegmentIndex = 0;
                                                let minDistance = Infinity;
                                                
                                                for (let i = 0; i < waypoints.length - 1; i++) {
                                                    const fromWaypoint = waypoints[i];
                                                    const toWaypoint = waypoints[i + 1];
                                                    
                                                    // Calculate distance from click point to line segment between waypoints
                                                    const dx = toWaypoint[1] - fromWaypoint[1];
                                                    const dy = toWaypoint[0] - fromWaypoint[0];
                                                    const length = Math.sqrt(dx * dx + dy * dy);
                                                    
                                                    if (length === 0) continue;
                                                    
                                                    const t = Math.max(0, Math.min(1, 
                                                        ((clickPoint[0] - fromWaypoint[0]) * dy + (clickPoint[1] - fromWaypoint[1]) * dx) / (length * length)
                                                    ));
                                                    
                                                    const closestPoint: [number, number] = [
                                                        fromWaypoint[0] + t * dy,
                                                        fromWaypoint[1] + t * dx
                                                    ];
                                                    
                                                    const distance = getDistanceMeters(clickPoint as [number, number], closestPoint);
                                                    if (distance < minDistance) {
                                                        minDistance = distance;
                                                        closestSegmentIndex = i;
                                                    }
                                                }
                                                
                                                // Create new waypoint at click point
                                                const newWaypoint: [number, number] = [clickPoint[0], clickPoint[1]];
                                                const insertIndex = closestSegmentIndex + 1;
                                                
                                                // Check if insertion point is near a stop (prevent editing at stop locations)
                                                if (isNearStop(newWaypoint, route, stops)) {
                                                    // Don't allow insertion near stops
                                                    return;
                                                }
                                                
                                                // Check minimum distance constraint
                                                if (insertIndex > 0) {
                                                    const distance = getDistanceMeters(newWaypoint, waypoints[insertIndex - 1]);
                                                    if (distance < MIN_VERTEX_DISTANCE_METERS) {
                                                        return;
                                                    }
                                                }
                                                if (insertIndex < waypoints.length) {
                                                    const distance = getDistanceMeters(newWaypoint, waypoints[insertIndex]);
                                                    if (distance < MIN_VERTEX_DISTANCE_METERS) {
                                                        return;
                                                    }
                                                }
                                                
                                                // Insert new waypoint
                                                const updatedWaypoints = [...waypoints];
                                                updatedWaypoints.splice(insertIndex, 0, newWaypoint);
                                                
                                                // Disable map dragging during edit
                                                map.dragging.disable();
                                                
                                                // Store old waypoints and geometry
                                                const oldWaypoints = [...waypoints];
                                                const oldGeometry = route.geometry?.coordinates || [];
                                                
                                                // Update route with new waypoint
                                                const routeWithNewWaypoint = {
                                                    ...route,
                                                    waypoints: updatedWaypoints,
                                                    isManuallyEdited: true,
                                                };
                                                updateRoute(routeWithNewWaypoint);
                                                
                                                // Show loading indicator
                                                setDragPreviewByRouteId((prev) => ({
                                                    ...prev,
                                                    [route.id]: buildPreviewGeometryFromWaypoints(updatedWaypoints, !!route.isCirculator),
                                                }));
                                                setRecalculatingRouteId(route.id);
                                                setRouteGenerationStatus({ isGenerating: true, routeName: route.name });
                                                
                                                try {
                                                    // Route only affected segments (the two segments around the new waypoint)
                                                    const existingGeometry = route.geometry?.coordinates as [number, number][] | undefined;
                                                    const routedGeometry = await routeAffectedSegments(
                                                        insertIndex,
                                                        updatedWaypoints,
                                                        route,
                                                        existingGeometry
                                                    );
                                                    
                                                    if (routedGeometry && routedGeometry.length > 0) {
                                                        // Update line with routed geometry
                                                        moveLine(line.id, routedGeometry, false);
                                                        
                                                        // Update route with geometry
                                                        const finalRoute = {
                                                            ...routeWithNewWaypoint,
                                                            geometry: {
                                                                coordinates: routedGeometry,
                                                                calculatedAt: Date.now(),
                                                                stopsHash: route.geometry?.stopsHash || '',
                                                            },
                                                        };
                                                        updateRoute(finalRoute);
                                                    }
                                                } finally {
                                                    // Hide loading indicator
                                                    setRecalculatingRouteId(null);
                                                    setRouteGenerationStatus({ isGenerating: false, routeName: null });
                                                    setDragPreviewByRouteId((prev) => {
                                                        const next = { ...prev };
                                                        delete next[route.id];
                                                        return next;
                                                    });
                                                }
                                                
                                                // Track drag state
                                                let draggedWaypointIndex = insertIndex;
                                                
                                                const handleDragMove = async (moveEvent: any) => {
                                                    moveEvent.originalEvent.preventDefault();
                                                    moveEvent.originalEvent.stopPropagation();
                                                    
                                                    const dragLatlng = moveEvent.latlng;
                                                    const newCoord: [number, number] = [dragLatlng.lat, dragLatlng.lng];
                                                    
                                                    // Check if dragged position is near a stop
                                                    if (isNearStop(newCoord, route, stops)) {
                                                        // Don't allow dragging near stops
                                                        return;
                                                    }
                                                    
                                                    // Update waypoint position
                                                    const currentWaypoints = [...updatedWaypoints];
                                                    currentWaypoints[draggedWaypointIndex] = newCoord;
                                                    
                                                    // Check distance to adjacent waypoints
                                                    if (draggedWaypointIndex > 0) {
                                                        const distance = getDistanceMeters(newCoord, currentWaypoints[draggedWaypointIndex - 1]);
                                                        if (distance < MIN_VERTEX_DISTANCE_METERS) {
                                                            return;
                                                        }
                                                    }
                                                    if (draggedWaypointIndex < currentWaypoints.length - 1) {
                                                        const distance = getDistanceMeters(newCoord, currentWaypoints[draggedWaypointIndex + 1]);
                                                        if (distance < MIN_VERTEX_DISTANCE_METERS) {
                                                            return;
                                                        }
                                                    }
                                                    
                                                    // Update route with new waypoint position
                                                    const tempRoute = {
                                                        ...route,
                                                        waypoints: currentWaypoints,
                                                        isManuallyEdited: true,
                                                    };
                                                    setDragPreviewByRouteId((prev) => ({
                                                        ...prev,
                                                        [route.id]: buildPreviewGeometryFromWaypoints(currentWaypoints, !!route.isCirculator),
                                                    }));
                                                    updateRoute(tempRoute);
                                                    
                                                    // For real-time preview, show straight line (full routing happens on drag end)
                                                    // Could optimize this later to route in real-time
                                                };
                                                
                                                const handleDragEnd = async (upEvent: any) => {
                                                    upEvent.originalEvent.preventDefault();
                                                    upEvent.originalEvent.stopPropagation();
                                                    
                                                    // Re-enable map dragging
                                                    map.dragging.enable();
                                                    
                                                    // Remove event listeners
                                                    map.off('mousemove', handleDragMove);
                                                    map.off('mouseup', handleDragEnd);
                                                    
                                                    // Get final waypoint position
                                                    const dragLatlng = upEvent.latlng;
                                                    const finalCoord: [number, number] = [dragLatlng.lat, dragLatlng.lng];
                                                    
                                                    // Update waypoints with final position
                                                    const finalWaypoints = [...updatedWaypoints];
                                                    finalWaypoints[draggedWaypointIndex] = finalCoord;
                                                    
                                                    // Show loading indicator
                                                    setDragPreviewByRouteId((prev) => ({
                                                        ...prev,
                                                        [route.id]: buildPreviewGeometryFromWaypoints(finalWaypoints, !!route.isCirculator),
                                                    }));
                                                    setRecalculatingRouteId(route.id);
                                                    setRouteGenerationStatus({ isGenerating: true, routeName: route.name });
                                                    
                                                    try {
                                                        // Route only affected segments for better performance
                                                        const existingGeometry = route.geometry?.coordinates as [number, number][] | undefined;
                                                        const routedGeometry = await routeAffectedSegments(
                                                            draggedWaypointIndex,
                                                            finalWaypoints,
                                                            route,
                                                            existingGeometry
                                                        );
                                                        
                                                        if (!routedGeometry || routedGeometry.length === 0) {
                                                            console.error('[MapLineFunctions] Failed to route affected segments');
                                                            return;
                                                        }
                                                        
                                                        // Update route with final waypoints and geometry
                                                        const finalRoute = {
                                                            ...route,
                                                            waypoints: finalWaypoints,
                                                            geometry: {
                                                                coordinates: routedGeometry,
                                                                calculatedAt: Date.now(),
                                                                stopsHash: route.geometry?.stopsHash || '',
                                                            },
                                                            isManuallyEdited: true,
                                                        };
                                                        updateRoute(finalRoute);
                                                        
                                                        // Update line on map
                                                        moveLine(line.id, routedGeometry, false);
                                                        
                                                        // Dispatch event to trigger segment recalculation.
                                                        // Translate the waypoint index to the stop-segment index it belongs to;
                                                        // passing the raw waypoint index refreshed the wrong segment's metrics
                                                        // when the route had intermediate (non-stop) waypoints.
                                                        const affectedSegmentIndex = waypointIndexToSegmentIndex(
                                                            route,
                                                            stops,
                                                            finalWaypoints,
                                                            draggedWaypointIndex
                                                        );
                                                        window.dispatchEvent(new CustomEvent('fixedRouteGeometryEdited', {
                                                            detail: {
                                                                routeId: route.id,
                                                                coordinates: routedGeometry,
                                                                oldCoordinates: oldGeometry,
                                                                affectedSegmentIndex,
                                                                skipGeometryRecalc: true,
                                                            }
                                                        }));
                                                    } finally {
                                                        // Hide loading indicator
                                                        setRecalculatingRouteId(null);
                                                        setRouteGenerationStatus({ isGenerating: false, routeName: null });
                                                        setDragPreviewByRouteId((prev) => {
                                                            const next = { ...prev };
                                                            delete next[route.id];
                                                            return next;
                                                        });
                                                    }
                                                };
                                                
                                                // Add event listeners for drag
                                                map.on('mousemove', handleDragMove);
                                                map.on('mouseup', handleDragEnd);
                                            }
                                        } : {})
                                    };

                        const osmRouteMeta = isOsmRouteLine(line) ? line.osmRouteMetadata : null;

                        return (
                            <React.Fragment key={`line-${line.id}-${layerIndex}-${shapeIndex}`}>
                                {/* For OSM transit routes we skip the invisible 40px hit-area
                                    Polyline — with hundreds of routes those extra SVG paths
                                    make pan hit-testing visibly laggy. The visible line is
                                    weight 4+ so it's easy to click directly; events + popup
                                    are attached to the visible Polyline below. */}
                                {!osmRouteMeta && (
                                    <Polyline
                                        positions={positions}
                                        pathOptions={{
                                            color: 'transparent',
                                            weight: isInEditMode ? 60 : 40,
                                            opacity: 0,
                                        }}
                                        eventHandlers={eventHandlers}
                                    />
                                )}
                                {/* Visible line */}
                                <Polyline
                                    positions={positions}
                                    pathOptions={{
                                        color: lineColor,
                                        weight: lineWeight,
                                        opacity: lineOpacity,
                                        dashArray: isInEditMode ? '10, 5' : (line.dashPattern === 'none' ? undefined : line.dashPattern),
                                    }}
                                    eventHandlers={osmRouteMeta ? eventHandlers : undefined}
                                >
                                    {osmRouteMeta && (
                                        <Popup>
                                            <OsmRoutePopupContent meta={osmRouteMeta} />
                                        </Popup>
                                    )}
                                </Polyline>
                                {isFixedRoute && route && dragPreviewPositions && dragPreviewPositions.length >= 2 && (
                                    <Polyline
                                        positions={dragPreviewPositions as LatLngTuple[]}
                                        pathOptions={{
                                            // Same appearance as every other loading line (capture preview,
                                            // circulator return) so the "dotted loading line" reads identically
                                            // everywhere. Dotted (inline) + throbbing (fr-loading-dash animation),
                                            // persists until real geometry returns.
                                            color: '#1e88e5',
                                            weight: 4,
                                            opacity: 0.85,
                                            dashArray: '2 10',
                                            className: 'fr-loading-dash',
                                        }}
                                    />
                                )}
                                {showCirculatorReturnLoading && (
                                    <Polyline
                                        positions={[lastPos, firstPos]}
                                        pathOptions={{
                                            color: '#1e88e5',
                                            weight: 4,
                                            opacity: 0.85,
                                            dashArray: '2 10',
                                            className: 'fr-loading-dash',
                                            interactive: false,
                                        }}
                                    />
                                )}
                                {/* Render loading indicator for route being recalculated */}
                                {isFixedRoute && route && recalculatingRouteId === route.id && (
                                    <Marker
                                        position={positions[Math.floor(positions.length / 2)]}
                                        icon={new DivIcon({
                                            className: 'route-recalculating-indicator',
                                            html: `
                                                <div style="
                                                    background: rgba(33, 150, 243, 0.9);
                                                    color: white;
                                                    padding: 8px 12px;
                                                    border-radius: 4px;
                                                    font-size: 12px;
                                                    font-weight: bold;
                                                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                                                    display: flex;
                                                    align-items: center;
                                                    gap: 8px;
                                                    white-space: nowrap;
                                                ">
                                                    <div style="
                                                        width: 16px;
                                                        height: 16px;
                                                        border: 2px solid white;
                                                        border-top-color: transparent;
                                                        border-radius: 50%;
                                                        animation: spin 1s linear infinite;
                                                    "></div>
                                                    Snapping to roads...
                                                </div>
                                                <style>
                                                    @keyframes spin {
                                                        to { transform: rotate(360deg); }
                                                    }
                                                </style>
                                            `,
                                            iconSize: [200, 40],
                                            iconAnchor: [100, 20],
                                        })}
                                        interactive={false}
                                    />
                                )}
                                
                                {/* Render waypoints for fixed routes in edit mode */}
                                {isInEditMode && route && (() => {
                                    // Ensure waypoints are initialized
                                    const waypoints = ensureWaypointsInitialized(route, stops);
                                    if (waypoints.length === 0) {
                                        return null;
                                    }
                                    
                                    // Identify which waypoints are stops (locked)
                                    const stopWaypointIndices = getWaypointIndicesForStops(route, stops, waypoints);
                                    
                                    return waypoints
                                        .map((waypoint, waypointIndex) => {
                                            // Validate waypoint
                                            if (!waypoint || !Array.isArray(waypoint) || waypoint.length < 2 ||
                                                typeof waypoint[0] !== 'number' || typeof waypoint[1] !== 'number' ||
                                                isNaN(waypoint[0]) || isNaN(waypoint[1])) {
                                                console.warn(`[MapLineFunctions] Invalid waypoint at index ${waypointIndex}, skipping`);
                                                return null;
                                            }
                                            
                                            const isLocked = stopWaypointIndices.has(waypointIndex);
                                            const waypointPos: LatLngTuple = [waypoint[0], waypoint[1]];
                                            
                                            // Visual distinction: locked waypoints (stops) vs unlocked (draggable)
                                            const waypointColor = isLocked ? '#3388ff' : '#ff4500'; // Blue for stops, orange for draggable
                                            const waypointFillColor = isLocked ? '#ffffff' : '#ffffff';
                                            
                                            return (
                                                <CircleMarker
                                                    key={`waypoint-${line.id}-${waypointIndex}-${waypoint[0]}-${waypoint[1]}`}
                                                center={waypointPos}
                                                radius={isLocked ? 8 : 6} // Larger for stops
                                                pathOptions={{
                                                    color: waypointColor,
                                                    weight: isLocked ? 3 : 2, // Thicker border for stops
                                                    fillColor: waypointFillColor,
                                                    fillOpacity: 1,
                                                }}
                                                eventHandlers={{
                                                    mousedown: (e: any) => {
                                                        // Prevent dragging locked waypoints (stops)
                                                        if (isLocked) {
                                                            e.originalEvent.preventDefault();
                                                            e.originalEvent.stopPropagation();
                                                            return;
                                                        }
                                                        
                                                        e.originalEvent.preventDefault();
                                                        e.originalEvent.stopPropagation();
                                                        
                                                        const map = e.target._map;
                                                        map.dragging.disable();
                                                        
                                                        // Store old waypoints and geometry
                                                        const oldWaypoints = [...waypoints];
                                                        const oldGeometry = route.geometry?.coordinates || [];
                                                        
                                                        const handleWaypointDrag = (moveEvent: any) => {
                                                            moveEvent.originalEvent.preventDefault();
                                                            moveEvent.originalEvent.stopPropagation();
                                                            
                                                            const dragLatlng = moveEvent.latlng;
                                                            const newCoord: [number, number] = [dragLatlng.lat, dragLatlng.lng];
                                                            
                                                            // Validate coordinate
                                                            if (typeof newCoord[0] !== 'number' || typeof newCoord[1] !== 'number' ||
                                                                isNaN(newCoord[0]) || isNaN(newCoord[1]) ||
                                                                !isFinite(newCoord[0]) || !isFinite(newCoord[1])) {
                                                                return;
                                                            }
                                                            
                                                            // Check if dragged position is near a stop
                                                            if (isNearStop(newCoord, route, stops)) {
                                                                // Don't allow dragging near stops
                                                                return;
                                                            }
                                                            
                                                            // Get current waypoints from route (may have been updated)
                                                            const currentRoute = routesRef.current.find(r => r.id === route.id);
                                                            const currentWaypoints = currentRoute?.waypoints || waypoints;
                                                            
                                                            // Check minimum distance constraint
                                                            const updatedWaypoints = [...currentWaypoints];
                                                            updatedWaypoints[waypointIndex] = newCoord;
                                                            
                                                            // Check distance to adjacent waypoints
                                                            if (waypointIndex > 0) {
                                                                const distance = getDistanceMeters(newCoord, updatedWaypoints[waypointIndex - 1]);
                                                                if (distance < MIN_VERTEX_DISTANCE_METERS) {
                                                                    return;
                                                                }
                                                            }
                                                            if (waypointIndex < updatedWaypoints.length - 1) {
                                                                const distance = getDistanceMeters(newCoord, updatedWaypoints[waypointIndex + 1]);
                                                                if (distance < MIN_VERTEX_DISTANCE_METERS) {
                                                                    return;
                                                                }
                                                            }
                                                            
                                                            // For real-time preview, only update affected segments with straight lines
                                                            // Keep the rest of the route geometry unchanged
                                                            const currentGeometry = currentRoute?.geometry?.coordinates as [number, number][] | undefined;
                                                            let previewGeometry: [number, number][];
                                                            
                                                            if (currentGeometry && currentGeometry.length > 0 && updatedWaypoints.length > 2) {
                                                                // Find waypoint positions in existing geometry by finding closest points
                                                                const waypointGeometryIndices: number[] = [];
                                                                for (const waypoint of updatedWaypoints) {
                                                                    let closestIdx = 0;
                                                                    let minDist = Infinity;
                                                                    for (let i = 0; i < currentGeometry.length; i++) {
                                                                        const dist = getDistanceMeters(waypoint, currentGeometry[i]);
                                                                        if (dist < minDist) {
                                                                            minDist = dist;
                                                                            closestIdx = i;
                                                                        }
                                                                    }
                                                                    waypointGeometryIndices.push(closestIdx);
                                                                }
                                                                
                                                                // Build hybrid geometry: keep existing segments, only replace affected ones
                                                                previewGeometry = [];
                                                                
                                                                // Add geometry before first affected segment
                                                                if (waypointIndex > 0) {
                                                                    const beforeEndIdx = waypointGeometryIndices[waypointIndex - 1];
                                                                    previewGeometry.push(...currentGeometry.slice(0, beforeEndIdx + 1));
                                                                }
                                                                
                                                                // Add affected segment 1: previous waypoint to dragged waypoint (straight line)
                                                                if (waypointIndex > 0) {
                                                                    const prevWaypoint = updatedWaypoints[waypointIndex - 1];
                                                                    previewGeometry.push(prevWaypoint, newCoord);
                                                                } else {
                                                                    // First waypoint: just add the dragged position
                                                                    previewGeometry.push(newCoord);
                                                                }
                                                                
                                                                // Add affected segment 2: dragged waypoint to next waypoint (straight line)
                                                                if (waypointIndex < updatedWaypoints.length - 1) {
                                                                    const nextWaypoint = updatedWaypoints[waypointIndex + 1];
                                                                    previewGeometry.push(nextWaypoint);
                                                                }
                                                                
                                                                // Add geometry after last affected segment
                                                                if (waypointIndex < updatedWaypoints.length - 1) {
                                                                    const afterStartIdx = waypointGeometryIndices[waypointIndex + 1];
                                                                    // Skip first point to avoid duplication (already added as nextWaypoint)
                                                                    previewGeometry.push(...currentGeometry.slice(afterStartIdx + 1));
                                                                }
                                                                
                                                                // Ensure we have valid geometry
                                                                if (previewGeometry.length < 2) {
                                                                    // Fallback: use waypoints
                                                                    previewGeometry = updatedWaypoints;
                                                                }
                                                            } else {
                                                                // Fallback: build preview geometry by connecting all waypoints with straight lines
                                                                previewGeometry = [];
                                                                for (let i = 0; i < updatedWaypoints.length - 1; i++) {
                                                                    if (i === 0) {
                                                                        previewGeometry.push(updatedWaypoints[i]);
                                                                    }
                                                                    previewGeometry.push(updatedWaypoints[i + 1]);
                                                                }
                                                            }
                                                            
                                                            // Update line with preview geometry
                                                            setDragPreviewByRouteId((prev) => ({
                                                                ...prev,
                                                                [route.id]: previewGeometry,
                                                            }));
                                                            moveLine(line.id, previewGeometry, false);
                                                        };
                                                        
                                                        const handleWaypointDragEnd = async (upEvent: any) => {
                                                            upEvent.originalEvent.preventDefault();
                                                            upEvent.originalEvent.stopPropagation();
                                                            
                                                            map.dragging.enable();
                                                            map.off('mousemove', handleWaypointDrag);
                                                            map.off('mouseup', handleWaypointDragEnd);
                                                            
                                                            // Get final waypoint position
                                                            const dragLatlng = upEvent.latlng;
                                                            const finalCoord: [number, number] = [dragLatlng.lat, dragLatlng.lng];
                                                            
                                                            // Validate final coordinate
                                                            if (typeof finalCoord[0] !== 'number' || typeof finalCoord[1] !== 'number' ||
                                                                isNaN(finalCoord[0]) || isNaN(finalCoord[1]) ||
                                                                !isFinite(finalCoord[0]) || !isFinite(finalCoord[1])) {
                                                                console.error('[MapLineFunctions] Invalid final coordinate after drag');
                                                                return;
                                                            }
                                                            
                                                            // Get current route and waypoints (may have changed)
                                                            const currentRoute = routesRef.current.find(r => r.id === route.id);
                                                            if (!currentRoute) {
                                                                console.error('[MapLineFunctions] Route not found after drag');
                                                                return;
                                                            }
                                                            
                                                            // Get current waypoints
                                                            const currentWaypoints = ensureWaypointsInitialized(currentRoute, stops);
                                                            const updatedWaypoints = [...currentWaypoints];
                                                            updatedWaypoints[waypointIndex] = finalCoord;
                                                            
                                                            // Validate waypoints before routing
                                                            const validWaypoints = updatedWaypoints.filter(wp => 
                                                                Array.isArray(wp) && 
                                                                wp.length === 2 && 
                                                                typeof wp[0] === 'number' && 
                                                                typeof wp[1] === 'number' &&
                                                                !isNaN(wp[0]) && !isNaN(wp[1]) &&
                                                                isFinite(wp[0]) && isFinite(wp[1])
                                                            ) as [number, number][];
                                                            
                                                            if (validWaypoints.length < 2) {
                                                                console.error('[MapLineFunctions] Not enough valid waypoints after drag');
                                                                return;
                                                            }
                                                            
                                                            // Show loading indicator
                                                            setDragPreviewByRouteId((prev) => ({
                                                                ...prev,
                                                                [currentRoute.id]: buildPreviewGeometryFromWaypoints(validWaypoints, !!currentRoute.isCirculator),
                                                            }));
                                                            setRecalculatingRouteId(currentRoute.id);
                                                            setRouteGenerationStatus({ isGenerating: true, routeName: currentRoute.name });
                                                            
                                                            try {
                                                                // Route only affected segments for better performance
                                                                console.log(`[MapLineFunctions] Routing affected segments for waypoint ${waypointIndex} of route ${currentRoute.id}`);
                                                                const existingGeometry = currentRoute.geometry?.coordinates as [number, number][] | undefined;
                                                                const routedGeometry = await routeAffectedSegments(
                                                                    waypointIndex,
                                                                    validWaypoints,
                                                                    currentRoute,
                                                                    existingGeometry
                                                                );
                                                                
                                                                if (!routedGeometry || routedGeometry.length === 0) {
                                                                    console.error('[MapLineFunctions] Failed to route between waypoints, keeping preview geometry');
                                                                    // Keep the preview geometry (straight lines) as fallback
                                                                    const fallbackGeometry: [number, number][] = [];
                                                                    for (let i = 0; i < validWaypoints.length - 1; i++) {
                                                                        if (i === 0) {
                                                                            fallbackGeometry.push(validWaypoints[i]);
                                                                        }
                                                                        fallbackGeometry.push(validWaypoints[i + 1]);
                                                                    }
                                                                    
                                                                    const fallbackRoute = {
                                                                        ...currentRoute,
                                                                        waypoints: validWaypoints,
                                                                        geometry: {
                                                                            coordinates: fallbackGeometry,
                                                                            calculatedAt: Date.now(),
                                                                            stopsHash: currentRoute.geometry?.stopsHash || '',
                                                                        },
                                                                        isManuallyEdited: true,
                                                                    };
                                                                    updateRoute(fallbackRoute);
                                                                    moveLine(line.id, fallbackGeometry, false);
                                                                    return;
                                                                }
                                                                
                                                                // Update route with new waypoints and geometry
                                                                const updatedRoute = {
                                                                    ...currentRoute,
                                                                    waypoints: validWaypoints,
                                                                    geometry: {
                                                                        coordinates: routedGeometry,
                                                                        calculatedAt: Date.now(),
                                                                        stopsHash: currentRoute.geometry?.stopsHash || '',
                                                                    },
                                                                    isManuallyEdited: true,
                                                                };
                                                                updateRoute(updatedRoute);
                                                                
                                                                // Update line on map
                                                                moveLine(line.id, routedGeometry, false);
                                                                
                                                                // Dispatch event to trigger segment recalculation.
                                                                // Translate the waypoint index to the stop-segment index it belongs to;
                                                                // passing the raw waypoint index refreshed the wrong segment's metrics
                                                                // when the route had intermediate (non-stop) waypoints.
                                                                const affectedSegmentIndex = waypointIndexToSegmentIndex(
                                                                    currentRoute,
                                                                    stops,
                                                                    currentWaypoints,
                                                                    waypointIndex
                                                                );
                                                                window.dispatchEvent(new CustomEvent('fixedRouteGeometryEdited', {
                                                                    detail: {
                                                                        routeId: currentRoute.id,
                                                                        coordinates: routedGeometry,
                                                                        oldCoordinates: oldGeometry,
                                                                        affectedSegmentIndex,
                                                                        skipGeometryRecalc: true,
                                                                    }
                                                                }));
                                                            } finally {
                                                                // Hide loading indicator
                                                                setRecalculatingRouteId(null);
                                                                setRouteGenerationStatus({ isGenerating: false, routeName: null });
                                                                setDragPreviewByRouteId((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next[currentRoute.id];
                                                                    return next;
                                                                });
                                                            }
                                                        };
                                                        
                                                        map.on('mousemove', handleWaypointDrag);
                                                        map.on('mouseup', handleWaypointDragEnd);
                                                    }
                                                }}
                                            />
                                            );
                                        })
                                        .filter((marker): marker is React.ReactElement => marker !== null);
                                })()}
                                {/* Render arrows */}
                                {arrowPositions
                                  .filter((arrow) => {
                                    // Validate arrow position before rendering
                                    const pos = arrow.pos;
                                    return pos && 
                                        pos.length === 2 && 
                                        pos[0] !== undefined && 
                                        pos[1] !== undefined &&
                                        pos[0] !== null && 
                                        pos[1] !== null &&
                                        typeof pos[0] === 'number' && 
                                        typeof pos[1] === 'number' &&
                                        !isNaN(pos[0]) && 
                                        !isNaN(pos[1]) &&
                                        isFinite(pos[0]) && 
                                        isFinite(pos[1]);
                                  })
                                  .map((arrow, index) => (
                                    <Marker
                                        key={`arrow-${line.id}-${index}`}
                                        position={arrow.pos}
                                        icon={createArrowIcon(lineColor, arrow.angle)}
                                        interactive={false}
                                    />
                                ))}
                            </React.Fragment>
                        );
                    }
                    return null;
                })
            );
    };
}

// Function to render temporary line while drawing
export function useRenderTempLine() {
    const context = useContext(AppContext);

    if (!context) {
        throw new Error("useRenderTempLine must be used within the AppContext provider");
    }

    const { modeSettings } = context;
    const { drawingLinePoints } = modeSettings;

    return () => {
        if (drawingLinePoints && drawingLinePoints.length > 0) {
            const positions: LatLngTuple[] = drawingLinePoints.map(coord => 
                [coord[0], coord[1]] as LatLngTuple
            );

            return (
                <React.Fragment>
                    <Polyline
                        positions={positions}
                        pathOptions={{
                            color: '#3388ff',
                            weight: 3,
                            opacity: 0.5,
                            dashArray: '5, 5'
                        }}
                    />
                    {/* Render temporary vertices */}
                    {positions.map((pos, index) => (
                        <CircleMarker
                            key={`temp-vertex-${index}`}
                            center={pos}
                            radius={6}
                            pathOptions={{
                                color: '#3388ff',
                                fillColor: '#3388ff',
                                fillOpacity: 0.5,
                                weight: 2
                            }}
                        />
                    ))}
                </React.Fragment>
            );
        }
        return null;
    };
} 
